import React, { useState, useEffect } from 'react';
import { DollarSign, CreditCard, FileText, CheckCircle, AlertCircle, ChevronRight, Users, Building, Wallet } from 'lucide-react';


// Production-safe API host: same-origin in deployed builds, env var in preview.
const _ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const _ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const _isPreviewEnv = /preview\.emergentagent\.com$/.test(
  (typeof window !== 'undefined' && window.location.hostname) || ''
);
const API_HOST = _isPreviewEnv ? _ENV_BACKEND : (_ORIGIN || _ENV_BACKEND);

const TUITION_PRICES = {
  CNA: 2500.00,
  PHLEBOTOMY: 2500.00,
  EMT: 2500.00,
  PN_TECH: 5500.00,
  HOMELAND_SECURITY: 2500.00,
  COMBINED_HEALTHCARE: 12500.00,
};

const PROGRAM_NAMES = {
  CNA: 'Certified Medical Assistant',
  PHLEBOTOMY: 'Phlebotomy Technician',
  EMT: 'Emergency Medical Technician',
  PN_TECH: 'Practical Nurse Tech',
  HOMELAND_SECURITY: 'Master Homeland Security Specialist',
  COMBINED_HEALTHCARE: 'Combined Healthcare & Emergency Response',
};

export default function FinancialAid({ studentId, programCode, preselectedOption, onComplete }) {
  const [step, setStep] = useState(preselectedOption ? 2 : 1);
  const [selectedOption, setSelectedOption] = useState(preselectedOption);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [autoCheckoutTriggered, setAutoCheckoutTriggered] = useState(false);
  
  // Payment Plan State
  const [paymentPlan, setPaymentPlan] = useState({
    numInstallments: 4,
    downPayment: 0
  });
  
  // WIOA State
  const [wioaForm, setWioaForm] = useState({
    isUnemployed: false,
    isUnderemployed: false,
    isDislocatedWorker: false,
    isVeteran: false,
    isLowIncome: false,
    receivesPublicAssistance: false,
    caseManagerName: '',
    caseManagerEmail: '',
    caseManagerPhone: ''
  });
  
  // Credit Authorization State
  const [creditAuth, setCreditAuth] = useState({
    authorizeCreditCheck: false,
    studentName: '',
    studentEmail: '',
    studentPhone: ''
  });
  
  const tuition = TUITION_PRICES[programCode] || 2500;
  const programName = PROGRAM_NAMES[programCode] || programCode;
  
  // Auto-trigger checkout for Klarna and Stripe when preselected
  useEffect(() => {
    if (preselectedOption && !autoCheckoutTriggered && studentId) {
      setAutoCheckoutTriggered(true);
      
      // Auto-trigger checkout for klarna or pay_now
      if (preselectedOption === 'klarna') {
        setTimeout(() => handleKlarnaSubmit(), 500);
      } else if (preselectedOption === 'pay_now') {
        setTimeout(() => handlePayNowWithStripe(), 500);
      }
    }
  }, [preselectedOption, autoCheckoutTriggered, studentId]);
  
  const calculateMonthlyPayment = () => {
    const remaining = tuition - paymentPlan.downPayment;
    return (remaining / paymentPlan.numInstallments).toFixed(2);
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    setStep(2);
    setResult(null);
  };

  const handlePaymentPlanSubmit = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_HOST}/api/financial-aid/payment-plan/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: studentId,
          program_code: programCode,
          total_amount: tuition,
          num_installments: paymentPlan.numInstallments,
          down_payment: paymentPlan.downPayment
        })
      });
      const data = await response.json();
      setResult({ success: true, data, type: 'payment_plan' });
      setStep(3);
    } catch (error) {
      setResult({ success: false, error: error.message });
    }
    setLoading(false);
  };

  const handleWIOASubmit = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_HOST}/api/financial-aid/wioa/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: studentId,
          program_code: programCode,
          total_amount: tuition,
          is_unemployed: wioaForm.isUnemployed,
          is_underemployed: wioaForm.isUnderemployed,
          is_dislocated_worker: wioaForm.isDislocatedWorker,
          is_veteran: wioaForm.isVeteran,
          is_low_income: wioaForm.isLowIncome,
          receives_public_assistance: wioaForm.receivesPublicAssistance,
          case_manager_name: wioaForm.caseManagerName,
          case_manager_email: wioaForm.caseManagerEmail,
          case_manager_phone: wioaForm.caseManagerPhone
        })
      });
      const data = await response.json();
      setResult({ success: true, data, type: 'wioa' });
      setStep(3);
    } catch (error) {
      setResult({ success: false, error: error.message });
    }
    setLoading(false);
  };

  const handleCreditAuthSubmit = async () => {
    if (!creditAuth.authorizeCreditCheck) {
      alert('You must authorize the credit check to proceed');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_HOST}/api/financial-aid/credit-authorization`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: studentId,
          program_code: programCode,
          total_amount: tuition,
          student_name: creditAuth.studentName,
          student_email: creditAuth.studentEmail,
          student_phone: creditAuth.studentPhone,
          authorize_credit_check: creditAuth.authorizeCreditCheck
        })
      });
      const data = await response.json();
      setResult({ success: true, data, type: 'credit_auth' });
      setStep(3);
    } catch (error) {
      setResult({ success: false, error: error.message });
    }
    setLoading(false);
  };

  const handleKlarnaSubmit = async () => {
    setLoading(true);
    try {
      // Get user info from localStorage
      const storedUser = localStorage.getItem('user');
      const userInfo = storedUser ? JSON.parse(storedUser) : {};
      
      // Get the origin URL for success/cancel redirects
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API_HOST}/api/stripe/klarna-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: studentId,
          student_name: userInfo.full_name || creditAuth.studentName || 'Student',
          student_email: userInfo.email || creditAuth.studentEmail || '',
          program_code: programCode,
          origin_url: originUrl
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.checkout_url) {
        // Redirect to Stripe Klarna Checkout
        window.location.href = data.checkout_url;
      } else {
        throw new Error(data.detail || 'Failed to create Klarna checkout session');
      }
    } catch (error) {
      setResult({ success: false, error: error.message });
      setStep(3);
    }
    setLoading(false);
  };

  const handlePayNowWithStripe = async () => {
    setLoading(true);
    try {
      // Get user info from localStorage for Stripe
      const storedUser = localStorage.getItem('user');
      const userInfo = storedUser ? JSON.parse(storedUser) : {};
      
      // Get the origin URL for success/cancel redirects
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API_HOST}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: studentId,
          student_name: userInfo.full_name || 'Student',
          student_email: userInfo.email || '',
          program_code: programCode,
          payment_type: 'tuition',
          origin_url: originUrl
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        throw new Error(data.detail || 'Failed to create checkout session');
      }
    } catch (error) {
      setResult({ success: false, error: error.message });
      setStep(3);
    }
    setLoading(false);
  };

  return (
    <div className="financial-aid-container" style={{ maxWidth: '800px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
          <DollarSign size={32} style={{ color: '#1e40af' }} />
          <h2 style={{ margin: 0, color: '#1e40af' }}>Financial Aid & Payment Options</h2>
        </div>
        <p style={{ color: '#64748b', margin: 0 }}>
          {programName} • Total Tuition: <strong style={{ color: '#166534', fontSize: '1.25rem' }}>${tuition.toLocaleString()}</strong>
        </p>
      </div>

      {/* Step 1: Select Payment Option */}
      {step === 1 && (
        <div>
          <h3 style={{ marginBottom: '16px', color: '#374151' }}>Select a Payment Option</h3>
          
          <div style={{ display: 'grid', gap: '16px' }}>
            {/* Pay Full Tuition Now */}
            <div 
              onClick={() => handleOptionSelect('pay_now')}
              style={{ 
                border: '3px solid #22c55e', 
                borderRadius: '12px', 
                padding: '20px', 
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ background: '#22c55e', padding: '12px', borderRadius: '50%' }}>
                  <Wallet size={24} style={{ color: 'white' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ background: '#22c55e', color: 'white', padding: '4px 12px', borderRadius: '4px', fontSize: '0.875rem', fontWeight: 'bold' }}>RECOMMENDED</span>
                    <h4 style={{ margin: 0 }}>Pay Full Tuition Now</h4>
                  </div>
                  <p style={{ margin: '4px 0 0', color: '#166534', fontSize: '0.875rem' }}>
                    Pay securely with credit/debit card via Stripe
                  </p>
                  <p style={{ margin: '4px 0 0', color: '#166534', fontSize: '1rem', fontWeight: '600' }}>
                    ${tuition.toLocaleString()} - Start training immediately
                  </p>
                </div>
                <ChevronRight size={24} style={{ color: '#22c55e' }} />
              </div>
            </div>
            
            {/* Option A: Payment Plan */}
            <div 
              onClick={() => handleOptionSelect('payment_plan')}
              style={{ 
                border: '2px solid #e5e7eb', 
                borderRadius: '12px', 
                padding: '20px', 
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: '#ffffff'
              }}
              onMouseOver={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
              onMouseOut={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ background: '#dbeafe', padding: '12px', borderRadius: '50%' }}>
                  <CreditCard size={24} style={{ color: '#1e40af' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ background: '#1e40af', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>OPTION A</span>
                    <h4 style={{ margin: 0 }}>Payment Plan</h4>
                  </div>
                  <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.875rem' }}>
                    Pay in monthly installments directly to MedCare • 0% Interest
                  </p>
                  <p style={{ margin: '4px 0 0', color: '#166534', fontSize: '0.875rem', fontWeight: '500' }}>
                    As low as ${(tuition / 12).toFixed(2)}/month
                  </p>
                </div>
                <ChevronRight size={24} style={{ color: '#9ca3af' }} />
              </div>
            </div>
            
            {/* Option B: Klarna */}
            <div 
              onClick={() => handleOptionSelect('klarna')}
              style={{ 
                border: '2px solid #e5e7eb', 
                borderRadius: '12px', 
                padding: '20px', 
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: '#ffffff'
              }}
              onMouseOver={(e) => e.currentTarget.style.borderColor = '#f472b6'}
              onMouseOut={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ background: '#fce7f3', padding: '12px', borderRadius: '50%' }}>
                  <Building size={24} style={{ color: '#db2777' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ background: '#db2777', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>OPTION B</span>
                    <h4 style={{ margin: 0 }}>Klarna Financing</h4>
                  </div>
                  <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.875rem' }}>
                    Klarna pays MedCare upfront • You pay Klarna monthly
                  </p>
                  <p style={{ margin: '4px 0 0', color: '#9333ea', fontSize: '0.875rem', fontWeight: '500' }}>
                    No upfront payment • Flexible terms • 0-24.99% APR
                  </p>
                </div>
                <ChevronRight size={24} style={{ color: '#9ca3af' }} />
              </div>
            </div>
            
            {/* Option C: WIOA Grant */}
            <div 
              onClick={() => handleOptionSelect('wioa')}
              style={{ 
                border: '2px solid #e5e7eb', 
                borderRadius: '12px', 
                padding: '20px', 
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: '#ffffff'
              }}
              onMouseOver={(e) => e.currentTarget.style.borderColor = '#22c55e'}
              onMouseOut={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ background: '#dcfce7', padding: '12px', borderRadius: '50%' }}>
                  <Users size={24} style={{ color: '#166534' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ background: '#166534', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>OPTION C</span>
                    <h4 style={{ margin: 0 }}>WIOA Grant Funding</h4>
                  </div>
                  <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.875rem' }}>
                    Workforce Innovation & Opportunity Act grant may cover full tuition
                  </p>
                  <p style={{ margin: '4px 0 0', color: '#166534', fontSize: '0.875rem', fontWeight: '500' }}>
                    Up to 100% coverage if eligible
                  </p>
                </div>
                <ChevronRight size={24} style={{ color: '#9ca3af' }} />
              </div>
            </div>
            
            {/* Option D: Credit Authorization */}
            <div 
              onClick={() => handleOptionSelect('credit_auth')}
              style={{ 
                border: '2px solid #e5e7eb', 
                borderRadius: '12px', 
                padding: '20px', 
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: '#ffffff'
              }}
              onMouseOver={(e) => e.currentTarget.style.borderColor = '#f59e0b'}
              onMouseOut={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ background: '#fef3c7', padding: '12px', borderRadius: '50%' }}>
                  <FileText size={24} style={{ color: '#b45309' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ background: '#b45309', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold' }}>OPTION D</span>
                    <h4 style={{ margin: 0 }}>Credit Authorization</h4>
                  </div>
                  <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.875rem' }}>
                    Authorize MedCare to apply for financing on your behalf
                  </p>
                  <p style={{ margin: '4px 0 0', color: '#b45309', fontSize: '0.875rem', fontWeight: '500' }}>
                    Use when WIOA not available • Multiple lender options
                  </p>
                </div>
                <ChevronRight size={24} style={{ color: '#9ca3af' }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Option Details Form */}
      {step === 2 && (
        <div>
          <button 
            onClick={() => setStep(1)} 
            style={{ marginBottom: '16px', background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            ← Back to options
          </button>

          {/* Pay Now with Stripe */}
          {selectedOption === 'pay_now' && (
            <div className="card" style={{ padding: '24px' }}>
              <h3 style={{ marginBottom: '16px', color: '#166534' }}>Pay Full Tuition Now</h3>
              
              <div style={{ background: '#f0fdf4', padding: '16px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #86efac' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontWeight: '500', color: '#166534' }}>Program:</span>
                  <span style={{ fontWeight: '600' }}>{programName}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: '500', color: '#166534' }}>Total Amount:</span>
                  <span style={{ fontWeight: '700', fontSize: '1.5rem', color: '#166534' }}>${tuition.toLocaleString()}</span>
                </div>
              </div>
              
              <div style={{ background: '#eff6ff', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#1e40af' }}>
                  <strong>Secure Payment:</strong> You will be redirected to Stripe's secure checkout page to complete your payment. All major credit and debit cards are accepted.
                </p>
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ marginBottom: '8px', color: '#374151' }}>What happens after payment:</h4>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#64748b', fontSize: '0.875rem' }}>
                  <li style={{ marginBottom: '4px' }}>Immediate access to all training modules</li>
                  <li style={{ marginBottom: '4px' }}>Email confirmation with receipt</li>
                  <li style={{ marginBottom: '4px' }}>Full enrollment in {programName} program</li>
                  <li>Dedicated student support</li>
                </ul>
              </div>
              
              <button
                onClick={handlePayNowWithStripe}
                disabled={loading}
                data-testid="pay-now-stripe-btn"
                style={{ 
                  width: '100%', 
                  padding: '16px', 
                  background: loading ? '#9ca3af' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  fontSize: '1.1rem', 
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {loading ? (
                  <>Processing...</>
                ) : (
                  <>
                    <CreditCard size={20} />
                    Pay ${tuition.toLocaleString()} with Stripe
                  </>
                )}
              </button>
              
              <p style={{ textAlign: 'center', marginTop: '12px', fontSize: '0.75rem', color: '#9ca3af' }}>
                Powered by Stripe • PCI-DSS Compliant • 256-bit SSL Encryption
              </p>
            </div>
          )}

          {/* Payment Plan Form */}
          {selectedOption === 'payment_plan' && (
            <div className="card" style={{ padding: '24px' }}>
              <h3 style={{ marginBottom: '16px', color: '#1e40af' }}>Payment Plan Setup</h3>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Down Payment (Optional)</label>
                <input
                  type="number"
                  min="0"
                  max={tuition * 0.5}
                  step="100"
                  value={paymentPlan.downPayment}
                  onChange={(e) => setPaymentPlan({ ...paymentPlan, downPayment: parseFloat(e.target.value) || 0 })}
                  style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '1rem' }}
                />
                <p style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '4px' }}>
                  Recommended: 25% down (${(tuition * 0.25).toFixed(2)})
                </p>
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Number of Installments</label>
                <select
                  value={paymentPlan.numInstallments}
                  onChange={(e) => setPaymentPlan({ ...paymentPlan, numInstallments: parseInt(e.target.value) })}
                  style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '1rem' }}
                >
                  <option value="2">2 months</option>
                  <option value="3">3 months</option>
                  <option value="4">4 months</option>
                  <option value="6">6 months</option>
                  <option value="12">12 months</option>
                </select>
              </div>
              
              <div style={{ background: '#f0fdf4', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
                <p style={{ margin: 0, color: '#166534' }}>
                  <strong>Monthly Payment:</strong> ${calculateMonthlyPayment()}
                </p>
                <p style={{ margin: '8px 0 0', fontSize: '0.875rem', color: '#166534' }}>
                  Total: ${tuition.toLocaleString()} | Down: ${paymentPlan.downPayment.toLocaleString()} | Balance: ${(tuition - paymentPlan.downPayment).toLocaleString()}
                </p>
              </div>
              
              <button
                onClick={handlePaymentPlanSubmit}
                disabled={loading}
                style={{ 
                  width: '100%', 
                  padding: '14px', 
                  background: '#1e40af', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  fontSize: '1rem', 
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Processing...' : 'Create Payment Plan'}
              </button>
            </div>
          )}

          {/* Klarna Form */}
          {selectedOption === 'klarna' && (
            <div className="card" style={{ padding: '24px' }}>
              <h3 style={{ marginBottom: '16px', color: '#db2777' }}>Klarna Financing</h3>
              
              <div style={{ background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)', padding: '20px', borderRadius: '12px', marginBottom: '20px', border: '2px solid #db2777' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontWeight: '500', color: '#9d174d' }}>Program:</span>
                  <span style={{ fontWeight: '600' }}>{programName}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: '500', color: '#9d174d' }}>Total Tuition:</span>
                  <span style={{ fontWeight: '700', fontSize: '1.5rem', color: '#db2777' }}>${tuition.toLocaleString()}</span>
                </div>
              </div>
              
              <div style={{ background: '#fce7f3', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
                <h4 style={{ color: '#9d174d', marginBottom: '8px' }}>How Klarna Works:</h4>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#be185d', fontSize: '0.875rem' }}>
                  <li style={{ marginBottom: '6px' }}>Get instant approval decision</li>
                  <li style={{ marginBottom: '6px' }}>Klarna pays MedCare the full tuition</li>
                  <li style={{ marginBottom: '6px' }}>You pay Klarna in monthly installments</li>
                  <li style={{ marginBottom: '6px' }}>0% - 24.99% APR based on credit</li>
                  <li>Choose 6-24 month payment terms</li>
                </ul>
              </div>
              
              <div style={{ background: '#eff6ff', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#1e40af' }}>
                  <strong>Secure Checkout:</strong> You will be redirected to Klarna's secure checkout page powered by Stripe to complete your financing application.
                </p>
              </div>
              
              <button
                onClick={handleKlarnaSubmit}
                disabled={loading}
                data-testid="klarna-checkout-btn"
                style={{ 
                  width: '100%', 
                  padding: '16px', 
                  background: loading ? '#9ca3af' : 'linear-gradient(135deg, #db2777 0%, #be185d 100%)', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  fontSize: '1.1rem', 
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {loading ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Wallet size={20} />
                    Apply for Klarna Financing
                  </>
                )}
              </button>
              
              <p style={{ textAlign: 'center', marginTop: '12px', fontSize: '0.75rem', color: '#9ca3af' }}>
                Powered by Klarna via Stripe • Secure Checkout • Instant Decision
              </p>
            </div>
          )}

          {/* WIOA Form */}
          {selectedOption === 'wioa' && (
            <div className="card" style={{ padding: '24px' }}>
              <h3 style={{ marginBottom: '16px', color: '#166534' }}>WIOA Grant Eligibility</h3>
              
              <p style={{ marginBottom: '16px', color: '#64748b' }}>
                Check all that apply to determine your eligibility for WIOA funding:
              </p>
              
              <div style={{ display: 'grid', gap: '12px', marginBottom: '20px' }}>
                {[
                  { key: 'isUnemployed', label: 'I am currently unemployed' },
                  { key: 'isUnderemployed', label: 'I am underemployed (working part-time or below my skill level)' },
                  { key: 'isDislocatedWorker', label: 'I am a dislocated worker (lost job due to layoff, closure, etc.)' },
                  { key: 'isVeteran', label: 'I am a veteran' },
                  { key: 'isLowIncome', label: 'I meet low-income requirements' },
                  { key: 'receivesPublicAssistance', label: 'I receive public assistance (SNAP, TANF, etc.)' }
                ].map(item => (
                  <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: '#f9fafb', borderRadius: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={wioaForm[item.key]}
                      onChange={(e) => setWioaForm({ ...wioaForm, [item.key]: e.target.checked })}
                      style={{ width: '20px', height: '20px' }}
                    />
                    <span>{item.label}</span>
                  </label>
                ))}
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ marginBottom: '12px' }}>Case Manager Information (if assigned)</h4>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <input
                    type="text"
                    placeholder="Case Manager Name"
                    value={wioaForm.caseManagerName}
                    onChange={(e) => setWioaForm({ ...wioaForm, caseManagerName: e.target.value })}
                    style={{ padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                  />
                  <input
                    type="email"
                    placeholder="Case Manager Email"
                    value={wioaForm.caseManagerEmail}
                    onChange={(e) => setWioaForm({ ...wioaForm, caseManagerEmail: e.target.value })}
                    style={{ padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                  />
                  <input
                    type="tel"
                    placeholder="Case Manager Phone"
                    value={wioaForm.caseManagerPhone}
                    onChange={(e) => setWioaForm({ ...wioaForm, caseManagerPhone: e.target.value })}
                    style={{ padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                  />
                </div>
              </div>
              
              <button
                onClick={handleWIOASubmit}
                disabled={loading}
                style={{ 
                  width: '100%', 
                  padding: '14px', 
                  background: '#166534', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  fontSize: '1rem', 
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Processing...' : 'Submit WIOA Application'}
              </button>
            </div>
          )}

          {/* Credit Authorization Form */}
          {selectedOption === 'credit_auth' && (
            <div className="card" style={{ padding: '24px' }}>
              <h3 style={{ marginBottom: '16px', color: '#b45309' }}>Credit Authorization</h3>
              
              <div style={{ background: '#fef3c7', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#92400e' }}>
                  Use this option when WIOA funding is not available or you do not qualify. MedCare will apply for financing on your behalf through Klarna, Affirm, or other tuition lenders.
                </p>
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Full Legal Name</label>
                <input
                  type="text"
                  value={creditAuth.studentName}
                  onChange={(e) => setCreditAuth({ ...creditAuth, studentName: e.target.value })}
                  style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                />
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Email</label>
                <input
                  type="email"
                  value={creditAuth.studentEmail}
                  onChange={(e) => setCreditAuth({ ...creditAuth, studentEmail: e.target.value })}
                  style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                />
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Phone</label>
                <input
                  type="tel"
                  value={creditAuth.studentPhone}
                  onChange={(e) => setCreditAuth({ ...creditAuth, studentPhone: e.target.value })}
                  style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px' }}
                />
              </div>
              
              <div style={{ marginBottom: '20px', padding: '16px', background: '#fee2e2', borderRadius: '8px', border: '1px solid #fca5a5' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={creditAuth.authorizeCreditCheck}
                    onChange={(e) => setCreditAuth({ ...creditAuth, authorizeCreditCheck: e.target.checked })}
                    style={{ marginTop: '4px', width: '20px', height: '20px' }}
                  />
                  <span style={{ fontSize: '0.875rem', color: '#991b1b' }}>
                    <strong>CREDIT AUTHORIZATION:</strong> I authorize MedCare Nursing Institute & Training Academy Inc. to obtain my credit report and submit financing applications on my behalf to Klarna, Affirm, or other tuition financing providers. I understand this may result in credit inquiries appearing on my credit report.
                  </span>
                </label>
              </div>
              
              <button
                onClick={handleCreditAuthSubmit}
                disabled={loading || !creditAuth.authorizeCreditCheck}
                style={{ 
                  width: '100%', 
                  padding: '14px', 
                  background: creditAuth.authorizeCreditCheck ? '#b45309' : '#9ca3af', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  fontSize: '1rem', 
                  fontWeight: '600',
                  cursor: loading || !creditAuth.authorizeCreditCheck ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Processing...' : 'Submit Credit Authorization'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Result */}
      {step === 3 && result && (
        <div className="card" style={{ padding: '24px', textAlign: 'center' }}>
          {result.success ? (
            <>
              <CheckCircle size={64} style={{ color: '#22c55e', marginBottom: '16px' }} />
              <h3 style={{ color: '#166534', marginBottom: '8px' }}>Application Submitted!</h3>
              
              {result.type === 'payment_plan' && (
                <div style={{ textAlign: 'left', background: '#f0fdf4', padding: '16px', borderRadius: '8px', marginTop: '16px' }}>
                  <p><strong>Plan ID:</strong> {result.data.plan?.plan_id}</p>
                  <p><strong>Monthly Payment:</strong> ${result.data.plan?.monthly_payment}</p>
                  <p><strong>Total Installments:</strong> {result.data.plan?.num_installments}</p>
                </div>
              )}
              
              {result.type === 'wioa' && (
                <div style={{ textAlign: 'left', background: '#f0fdf4', padding: '16px', borderRadius: '8px', marginTop: '16px' }}>
                  <p><strong>Application ID:</strong> {result.data.application?.application_id}</p>
                  <p><strong>Status:</strong> {result.data.application?.eligibility_status}</p>
                  <p style={{ marginTop: '8px', fontSize: '0.875rem', color: '#64748b' }}>{result.data.eligibility_assessment}</p>
                </div>
              )}
              
              {(result.type === 'klarna' || result.type === 'credit_auth') && (
                <div style={{ textAlign: 'left', background: '#fce7f3', padding: '16px', borderRadius: '8px', marginTop: '16px' }}>
                  <p><strong>Authorization ID:</strong> {result.data.authorization?.authorization_id}</p>
                  <p><strong>Status:</strong> {result.data.authorization?.status}</p>
                  <h4 style={{ marginTop: '12px', marginBottom: '8px' }}>Next Steps:</h4>
                  <ul style={{ paddingLeft: '20px', margin: 0 }}>
                    {result.data.next_steps?.map((step, i) => (
                      <li key={i} style={{ fontSize: '0.875rem', marginBottom: '4px' }}>{step}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <>
              <AlertCircle size={64} style={{ color: '#ef4444', marginBottom: '16px' }} />
              <h3 style={{ color: '#dc2626' }}>Error</h3>
              <p style={{ color: '#64748b' }}>{result.error}</p>
            </>
          )}
          
          <button
            onClick={() => { setStep(1); setResult(null); setSelectedOption(null); }}
            style={{ marginTop: '20px', padding: '12px 24px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
          >
            Start Over
          </button>
        </div>
      )}
    </div>
  );
}
